import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getDepartments,
  getDepartmentById,
  createDepartment,
  updateDepartment,
  deleteDepartment
} from './departmentApi';

// Get all departments
export const useGetDepartments = () => {
  return useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        const data = await getDepartments();
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error fetching departments:', error);
        toast.error('Failed to fetch departments');
        throw error;
      }
    },
    staleTime: 30000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

// Get department by ID
export const useGetDepartmentById = (deptId) => {
  return useQuery({
    queryKey: ['departments', deptId],
    queryFn: () => getDepartmentById(deptId),
    enabled: !!deptId,
    onError: (error) => {
      console.error('Error fetching department:', error);
      toast.error(error.message || 'Failed to fetch department details');
    }
  });
};

// Create a new department
export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries(['departments']);
      toast.success('Department created successfully');
    },
    onError: (error) => {
      console.error('Error creating department:', error);
      toast.error(error.message || 'Failed to create department');
    }
  });
};

// Update a department
export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ departmentId, data }) => {
      if (!departmentId) throw new Error('Department ID is required');
      return updateDepartment(departmentId, data);
    },
    onSuccess: (_, { departmentId }) => {
      queryClient.invalidateQueries(['departments']);
      queryClient.invalidateQueries(['departments', departmentId]);
      toast.success('Department updated successfully');
    },
    onError: (error) => {
      console.error('Error updating department:', error);
      toast.error(error.message || 'Failed to update department');
    }
  });
};

// Delete a department
export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (deptId) => {
      if (!deptId) throw new Error('Department ID is required');
      return deleteDepartment(deptId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['departments']);
      toast.success('Department deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting department:', error);
      toast.error(error.message || 'Failed to delete department');
    }
  });
};